import { useState, useMemo } from 'react';
import { useNavigate, useMatch, useParams } from 'react-router-dom';
import type { UseMutationResult } from '@tanstack/react-query';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { ActionLink } from '@/components/primitives/ActionLink';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/primitives/Button';
import { TextInput } from '@/components/primitives/TextInput';
import {
  useClients,
  useCreateClient,
  useUpdateClient,
  useDeleteClient,
  useArchiveClient,
  useRestoreClient,
} from '@/hooks/useClients';
import {
  useCreateProject,
  useArchiveProject,
  useDeleteProject,
  useRestoreProject,
} from '@/hooks/useProjects';
import { ProjectForm } from '@/components/composites/ProjectForm';
import { formatMonth } from '@/lib/dates';
import type { Client } from '@/types/entities';
import type { StatCardModel } from '@/types/stats';
import styles from './ManageClientsPage.module.css';

// ── SlidePanel form (keyed by target to reset state on switch) ────────────

interface ClientFormProps {
  editingClient: Client | undefined;
  isNew: boolean;
  onClose: () => void;
  createClient: UseMutationResult<unknown, Error, { name: string; slug?: string }>;
  updateClient: UseMutationResult<unknown, Error, { id: string; name?: string; slug?: string }>;
  deleteClient: UseMutationResult<unknown, Error, string>;
  archiveClient: UseMutationResult<unknown, Error, string>;
  restoreClient: UseMutationResult<unknown, Error, string>;
}

function ClientForm({
  editingClient,
  isNew,
  onClose,
  createClient,
  updateClient,
  deleteClient,
  archiveClient,
  restoreClient,
}: ClientFormProps) {
  const [name, setName] = useState(editingClient?.name ?? '');
  const [slug, setSlug] = useState(
    editingClient ? editingClient.name.toLowerCase().replace(/\s+/g, '-') : '',
  );
  const [error, setError] = useState('');

  const isSaving = createClient.isPending || updateClient.isPending;
  const panelTitle = isNew ? 'Add Client' : 'Edit Client';

  const handleSave = () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setError('');

    if (editingClient) {
      updateClient.mutate(
        { id: editingClient.id, name: name.trim(), slug: slug.trim() || undefined },
        { onSuccess: onClose, onError: (err) => setError(err.message) },
      );
    } else {
      createClient.mutate(
        { name: name.trim(), slug: slug.trim() || undefined },
        { onSuccess: onClose, onError: (err) => setError(err.message) },
      );
    }
  };

  return (
    <SlidePanel
      open
      onClose={onClose}
      title={panelTitle}
      width="sm"
      footer={
        <div className={styles.panelFooter}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : isNew ? 'Save Client' : 'Save Changes'}
          </Button>
        </div>
      }
    >
      <div className={styles.panelBody}>
        {error && <div className={styles.formError}>{error}</div>}
        <TextInput
          label="Name"
          placeholder="Client name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextInput
          label="Slug"
          placeholder="auto-generated-from-name"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          hint={
            editingClient
              ? 'Changing the slug may break existing integrations.'
              : 'Auto-generated from name if left blank.'
          }
          className={styles.monoInput}
        />
        {!isNew && editingClient && editingClient.archivedAt ? (
          <div className={styles.archiveSection}>
            <h3 className={styles.archiveSectionTitle}>Archived</h3>
            <p className={styles.archiveSectionText}>
              This client is archived. Restoring brings it back to the active list. Rules paused
              during archive are not automatically re-enabled — re-enable each rule explicitly from
              the Rules page.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                restoreClient.mutate(editingClient.id, {
                  onSuccess: onClose,
                  onError: (err) => setError(err.message),
                });
              }}
              disabled={restoreClient.isPending}
            >
              {restoreClient.isPending ? 'Restoring...' : 'Restore Client'}
            </Button>
          </div>
        ) : null}
        {!isNew && editingClient && !editingClient.archivedAt && (
          <div className={styles.archiveSection}>
            <h3 className={styles.archiveSectionTitle}>Archive or delete</h3>
            {(editingClient.classifiedEventCount ?? 0) > 0 ? (
              <>
                <p className={styles.archiveSectionText}>
                  Archiving this client hides it from active views and cascade-pauses rules
                  targeting its projects. This action is reversible.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => {
                    archiveClient.mutate(editingClient.id, {
                      onSuccess: onClose,
                      onError: (err) => setError(err.message),
                    });
                  }}
                  disabled={archiveClient.isPending}
                >
                  {archiveClient.isPending ? 'Archiving...' : 'Archive Client'}
                </Button>
              </>
            ) : (
              <>
                <p className={styles.archiveSectionText}>
                  This client has no classified activity, so it can be deleted. Any child projects
                  and rules targeting them are removed. Deletion cannot be undone.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => {
                    deleteClient.mutate(editingClient.id, {
                      onSuccess: onClose,
                      onError: (err) => setError(err.message),
                    });
                  }}
                  disabled={deleteClient.isPending}
                >
                  {deleteClient.isPending ? 'Deleting...' : 'Delete Client'}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </SlidePanel>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export function ManageClientsPage() {
  const navigate = useNavigate();
  const newMatch = useMatch('/clients/new');
  const editMatch = useMatch('/clients/:clientId/edit');
  const newProjectMatch = useMatch('/clients/:clientId/projects/new');
  const { clientId: editClientId } = useParams<{ clientId: string }>();

  const [showArchived, setShowArchived] = useState(false);
  const { data: clients } = useClients({ includeArchived: showArchived });
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();
  const archiveClient = useArchiveClient();
  const restoreClient = useRestoreClient();
  const createProject = useCreateProject();
  const archiveProject = useArchiveProject();
  const deleteProject = useDeleteProject();
  const restoreProject = useRestoreProject();

  // All clients start expanded
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const c of clients) map[c.id] = true;
    return map;
  });

  const [search, setSearch] = useState('');

  // Only treat the URL's :clientId as "editing" when the edit route matches.
  // The Add-Project route (/clients/:clientId/projects/new) also populates
  // useParams().clientId, so without this gate the ClientForm panel would
  // attempt to mount on top of the ProjectForm.
  const editingClient =
    editMatch && editClientId ? clients.find((c) => c.id === editClientId) : undefined;
  const newProjectClient = newProjectMatch
    ? clients.find((c) => c.id === newProjectMatch.params.clientId)
    : undefined;
  // System-owned clients (e.g. the per-org Internal fallback) cannot be
  // edited or archived — the backend returns 409 SYSTEM_OWNED. Disabled
  // affordances in the table block normal navigation; this guards direct
  // URL navigation to the edit panel.
  const editPanelBlocked = !!editingClient && editingClient.systemOwned;
  const panelOpen = (!!newMatch || !!editMatch) && !editPanelBlocked;
  // Key forces form remount when switching between new/edit targets
  const formKey = editClientId ?? (newMatch ? '__new__' : '__closed__');

  const handleClientAction = (client: Client) => {
    if (client.systemOwned) return;
    if (client.archivedAt) {
      if (!window.confirm(`Restore client "${client.name}"?`)) return;
      restoreClient.mutate(client.id, {
        onError: (err) => window.alert(`Could not restore client: ${err.message}`),
      });
      return;
    }
    const hasActivity = (client.classifiedEventCount ?? 0) > 0;
    if (hasActivity) {
      if (
        !window.confirm(
          `Archive client "${client.name}"? All its projects will be archived and every rule targeting those projects will be paused. This can be undone.`,
        )
      ) {
        return;
      }
      archiveClient.mutate(client.id, {
        onError: (err) => window.alert(`Could not archive client: ${err.message}`),
      });
    } else {
      if (!window.confirm(`Delete client "${client.name}"? This cannot be undone.`)) return;
      deleteClient.mutate(client.id, {
        onError: (err) => window.alert(`Could not delete client: ${err.message}`),
      });
    }
  };

  const handleProjectAction = (proj: Client['projects'][number]) => {
    if (proj.systemOwned) return;
    if (proj.archivedAt) {
      if (!window.confirm(`Restore project "${proj.name}"?`)) return;
      restoreProject.mutate(proj.id, {
        onError: (err) => window.alert(`Could not restore project: ${err.message}`),
      });
      return;
    }
    const hasActivity = (proj.classifiedEventCount ?? 0) > 0;
    if (hasActivity) {
      if (
        !window.confirm(
          `Archive project "${proj.name}"? Rules targeting this project will be paused and can be re-enabled after restoring.`,
        )
      ) {
        return;
      }
      archiveProject.mutate(proj.id, {
        onError: (err) => window.alert(`Could not archive project: ${err.message}`),
      });
    } else {
      if (!window.confirm(`Delete project "${proj.name}"? This cannot be undone.`)) return;
      deleteProject.mutate(proj.id, {
        onError: (err) => window.alert(`Could not delete project: ${err.message}`),
      });
    }
  };

  const totalProjects = clients.reduce((s, c) => s + c.projects.length, 0);
  const newestClient = clients.length
    ? clients.reduce((a, b) => (a.createdAt > b.createdAt ? a : b))
    : null;

  const stats: [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Total Clients', value: String(clients.length) },
    { label: 'Active Projects', value: String(totalProjects) },
    {
      label: 'Avg Projects/Client',
      value: clients.length > 0 ? (totalProjects / clients.length).toFixed(1) : '0',
    },
    {
      label: 'Newest Client',
      value: newestClient ? formatMonth(newestClient.createdAt) : '\u2014',
    },
  ];

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const closePanel = () => navigate('/clients');

  // Filter clients by search, then pin system-owned rows (Internal) to
  // the bottom. Stable-sort keeps the backend's alpha order within each
  // partition.
  const filteredClients = useMemo(() => {
    const matched = search
      ? clients.filter(
          (c) =>
            c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.projects.some((p) => p.name.toLowerCase().includes(search.toLowerCase())),
        )
      : [...clients];
    return matched.sort((a, b) => Number(a.systemOwned) - Number(b.systemOwned));
  }, [clients, search]);

  return (
    <>
      <PageHeader
        title="Clients & Projects"
        actions={
          <>
            <label className={styles.archivedToggle}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
            <Button variant="primary" onClick={() => navigate('/clients/new')}>
              Add Client
            </Button>
          </>
        }
      />
      <div className={styles.summary}>
        {clients.length} clients &middot; {totalProjects} projects
      </div>
      <StatCardGrid cards={stats} />

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <svg
          className={styles.searchIcon}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search clients or projects..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Accordion Table */}
      <div className={styles.tableCard}>
        <table className={styles.table}>
          <caption className={styles.srOnly}>Clients and projects</caption>
          <thead>
            <tr>
              <th className={styles.th} style={{ width: '36%' }}>
                Client / Project
              </th>
              <th className={styles.th} style={{ width: '14%' }}>
                Type
              </th>
              <th className={styles.th} style={{ width: '18%' }}>
                Slug
              </th>
              <th className={styles.th} style={{ width: '14%' }}>
                Created
              </th>
              <th className={styles.th} style={{ width: '18%' }}>
                Actions
              </th>
            </tr>
          </thead>
          {filteredClients.map((client) => {
            const isOpen = !!openGroups[client.id];
            const slug = client.name.toLowerCase().replace(/\s+/g, '-');
            const hasProjects = client.projects.length > 0;
            const clientLocked = client.systemOwned;
            const lockedTitle = 'System-owned — managed by Kanjoy';
            return (
              <tbody key={client.id}>
                {/* Client row */}
                <tr className={styles.clientRow} onClick={() => toggleGroup(client.id)}>
                  <td className={styles.clientNameCell}>
                    <span className={styles.chevronWrap}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 200ms ease',
                        }}
                        aria-hidden="true"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </span>
                    <span className={styles.clientName}>{client.name}</span>
                    <span className={styles.projectCount}>
                      {client.projects.length}{' '}
                      {client.projects.length === 1 ? 'project' : 'projects'}
                    </span>
                  </td>
                  <td className={styles.clientCell}>&mdash;</td>
                  <td className={`${styles.clientCell} ${styles.slugCol}`}>{slug}</td>
                  <td className={`${styles.clientCell} ${styles.createdCol}`}>
                    {formatMonth(client.createdAt)}
                  </td>
                  <td className={`${styles.clientCell} ${styles.actionsCol}`}>
                    <ActionLink
                      disabled={clientLocked || !!client.archivedAt}
                      title={
                        clientLocked
                          ? lockedTitle
                          : client.archivedAt
                            ? 'Restore the client before editing'
                            : undefined
                      }
                      onClick={
                        clientLocked || client.archivedAt
                          ? undefined
                          : (e) => {
                              e.stopPropagation();
                              navigate(`/clients/${client.id}/edit`);
                            }
                      }
                    >
                      Edit
                    </ActionLink>
                    <span className={styles.actionSep}>&middot;</span>
                    {clientLocked ? (
                      <ActionLink danger disabled title={lockedTitle}>
                        Archive
                      </ActionLink>
                    ) : client.archivedAt ? (
                      <ActionLink
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClientAction(client);
                        }}
                      >
                        Restore
                      </ActionLink>
                    ) : (
                      <ActionLink
                        danger
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClientAction(client);
                        }}
                      >
                        {(client.classifiedEventCount ?? 0) > 0 || hasProjects
                          ? 'Archive'
                          : 'Delete'}
                      </ActionLink>
                    )}
                  </td>
                </tr>

                {/* Project rows */}
                {isOpen &&
                  client.projects.map((proj) => {
                    const projSlug = proj.name.toLowerCase().replace(/\s+/g, '-');
                    const projLocked = proj.systemOwned;
                    return (
                      <tr key={proj.id} className={styles.projectRow}>
                        <td className={styles.projectNameCell}>{proj.name}</td>
                        <td className={styles.projectCell}>
                          <Badge variant={proj.type}>
                            {proj.type[0].toUpperCase() + proj.type.slice(1)}
                          </Badge>
                        </td>
                        <td className={`${styles.projectCell} ${styles.slugCol}`}>{projSlug}</td>
                        <td className={`${styles.projectCell} ${styles.createdCol}`}>
                          {formatMonth(proj.createdAt)}
                        </td>
                        <td className={`${styles.projectCell} ${styles.actionsCol}`}>
                          <ActionLink
                            disabled={projLocked || !!proj.archivedAt}
                            title={
                              projLocked
                                ? lockedTitle
                                : proj.archivedAt
                                  ? 'Restore the project before editing'
                                  : undefined
                            }
                            onClick={
                              projLocked || proj.archivedAt
                                ? undefined
                                : () => navigate(`/projects/${proj.id}/edit`)
                            }
                          >
                            Edit
                          </ActionLink>
                          <span className={styles.actionSep}>&middot;</span>
                          {projLocked ? (
                            <ActionLink disabled title={lockedTitle}>
                              Archive
                            </ActionLink>
                          ) : proj.archivedAt ? (
                            <ActionLink onClick={() => handleProjectAction(proj)}>
                              Restore
                            </ActionLink>
                          ) : (
                            <ActionLink danger onClick={() => handleProjectAction(proj)}>
                              {(proj.classifiedEventCount ?? 0) > 0 ? 'Archive' : 'Delete'}
                            </ActionLink>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                {/* Add project row — real button, not a dead <td>. The
                    trigger owns the entire row width and carries the muted-
                    link visual weight via .addProjectTrigger (task 114). */}
                {isOpen && (
                  <tr className={styles.addProjectRow}>
                    <td colSpan={5} className={styles.addProjectCell}>
                      <button
                        type="button"
                        className={styles.addProjectTrigger}
                        onClick={() => navigate(`/clients/${client.id}/projects/new`)}
                      >
                        + Add project to {client.name}
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </div>

      {/* Slide Panel — Client add/edit */}
      {panelOpen && (
        <ClientForm
          key={formKey}
          editingClient={editingClient}
          isNew={!!newMatch}
          onClose={closePanel}
          createClient={createClient}
          updateClient={updateClient}
          deleteClient={deleteClient}
          archiveClient={archiveClient}
          restoreClient={restoreClient}
        />
      )}

      {/* Slide Panel — Add Project (task 114). Sibling of the ClientForm
          panel above; only one can be open at a time because the two URL
          matches are disjoint. */}
      {newProjectMatch && newProjectClient && (
        <ProjectForm
          key={`new-project-${newProjectClient.id}`}
          clientId={newProjectClient.id}
          clientName={newProjectClient.name}
          onClose={closePanel}
          createProject={createProject}
        />
      )}
    </>
  );
}
